/* eslint-disable no-template-curly-in-string */
import _ from 'lodash';
import { Checkbox, Label } from 'theme-ui';
import * as yup from 'yup';
import { getDefaultRedirectUrl } from '../../consts/default-redirect-url';
import { DemandPartnerCustomer, Employer } from '../../consts/models';
import { isUrlValid } from '../../utils/validations';
import { FieldConfigs } from '../Form/LinkTokenForm';
import { InputAccount } from '../Form/InputAccount';
import { InputJSON } from '../Form/InputJSON';
import { InputCard } from '../Form/InputCard';
import { InputSelect, MultiSelect } from '../Form/InputSelect';
import { InputText } from '../Form/InputText';
import { getEmptyOption, getOption } from '../Form/utils';
import { ModeKey, shouldEnableAmount } from './form-helpers';
import { validate as isValidUUID } from 'uuid';

import {
  ALL_JOBS,
  FEATURE_LAUNCH_TYPES,
  PLATFORM_TYPES,
  PLATFORM_TYPES_TAX,
  TLinkTokenForm,
  WRITE_JOBS,
} from './types';

// const eraseNonDigits = (s: string) => s.replace(/\D/g, '');
const eraseNonDigitsOrNonPeriods = (s: string) => s.replace(/[^\d|.]/g, '');

const digitsOnly = (value) => /^\d+$/.test(value);

const expirationDateRegex = new RegExp('(0[1-9]|10|11|12)/[0-9]{2}$');
const expirationDateFormat = (value) => expirationDateRegex.test(value);

const iso8601DateFormat = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

export type TestConsolePropGeneratorOptions = {
  availableModes: ModeKey[];
  setEmployerSearchText: (search: string) => void;
  employers: Employer[];
  lastAccountId?: string;
  workspaceId?: string | undefined | null;
  demandPartnerCustomers?: DemandPartnerCustomer[] | null;
};

const accountSchema = yup.array().of(
  yup.object({
    name: yup.string(),
    type: yup.string().oneOf(['checking', 'savings']).required(),
    routing_number: yup
      .string()
      .required('${path} is required')
      .test('Digits only', '${path} should have digits only', digitsOnly)
      .length(9, '${path} must have 9 digits'),
    account_number: yup
      .string()
      .required('${path} is required')
      .test('Digits only', '${path} should have digits only', digitsOnly)
      .min(5, '${path} must be at least 5 digits long'),
  })
);

const platformMatchingSchema = yup.object({
  mobile_phone_number: yup
    .string()
    .optional()
    .when({
      is: (exists: string) => !!exists,
      then: (rule) =>
        rule
          .test('Digits only', '${path} should have digits only', digitsOnly)
          .length(10, '${path} must have 10 digits'),
    }),
  date_of_birth: yup
    .string()
    .optional()
    .when({
      is: (exists: string) => !!exists,
      then: (rule) =>
        rule.test('YYYY-MM-DD format', '${path} should match format YYYY-MM-DD', iso8601DateFormat),
    }),
  social_security_number: yup
    .string()
    .optional()
    .when({
      is: (exists: string) => !!exists,
      then: (rule) =>
        rule
          .test('Digits only', '${path} should have digits only', digitsOnly)
          .length(9, '${path} must have 9 digits'),
    }),
  social_security_number_last_four: yup
    .string()
    .optional()
    .when({
      is: (exists: string) => !!exists,
      then: (rule) =>
        rule
          .test('Digits only', '${path} should have digits only', digitsOnly)
          .length(4, '${path} must have 4 digits'),
    }),
  first_name: yup.string().optional(),
  last_name: yup.string().optional(),
  home_address_zip_code: yup
    .string()
    .optional()
    .when({
      is: (exists: string) => !!exists,
      then: (rule) =>
        rule
          .test('Digits only', '${path} should have digits only', digitsOnly)
          .length(5, '${path} must have 5 digits'),
    }),
});

const cardSchema = yup.array().of(
  yup.object({
    card_name: yup.string().required(),
    card_number: yup
      .string()
      .required('${path} is required')
      .test('Digits only', '${path} should have digits only', digitsOnly)
      .min(8, '${path} must be at least 8 digits long')
      .max(19, '${path} must be 19 or fewer digits'),
    cvc: yup
      .string()
      .required('${path} is required')
      .test('Digits only', '${path} should have digits only', digitsOnly)
      .min(3, '${path} must be at least 3 digits long')
      .max(4, '${path} must be 4 or fewer digits'),
    expiration_date: yup
      .string()
      .required('${path} is required')
      .test('MM/YY format', '${path} should match format MM/YY', expirationDateFormat),
  })
);

export const formHasWriteJob = (form: TLinkTokenForm) =>
  form.requiredJobs?.some((j) => WRITE_JOBS.includes(j.value as any));

export const formHasDDSJob = (form: TLinkTokenForm) =>
  (form.requiredJobs?.map((x) => x.value) || []).includes('direct_deposit_switch');

export const formHasBillSwitchJob = (form: TLinkTokenForm) =>
  (form.requiredJobs?.map((x) => x.value) || []).includes('bill_switch');

export const formHasJobThatAcceptsAccounts = (form: TLinkTokenForm) => {
  const requiredJobs = form.requiredJobs?.map((x) => x.value) || [];
  return requiredJobs.some((job) => ['bill_switch', 'direct_deposit_switch'].includes(job));
};

// Use as a filler component if needed in for flex layout.
const EmptyBox = () => {
  return <div />;
};

const allJobsWithoutMerchantJobs = ALL_JOBS.filter(
  (job) => !['bill_switch', 'bill_cancellation'].includes(job)
);
export const requiredJobsWithoutMerchantJobs: FieldConfigs<
  TestConsolePropGeneratorOptions,
  TLinkTokenForm
> = {
  requiredJobs: {
    Component: MultiSelect,
    getError: (form) => {
      const employer = form.selectedEmployer?.value;
      const selectedJobs = form.requiredJobs?.map((x) => x.value) || [];

      if (_.isEmpty(employer)) {
        if (selectedJobs.length === 0) {
          return 'One job must be selected';
        }

        return null;
      }

      const supportedJobs = employer?.supportedJobs || [];
      const unsupportedJob = _.find(selectedJobs, (job) => !supportedJobs.includes(job));

      if (unsupportedJob) return `Employer does not support ${unsupportedJob}`;

      return null;
    },
    generateProps: ({ form }) => ({
      x: !form.accountId,
      /*
       * If supportedJobs return anything that is not supported by the Front-end,
       * it shouldn't display it to the user
       */
      options: _.intersection(
        _.get(form, 'selectedEmployer.value.supportedJobs', allJobsWithoutMerchantJobs),
        allJobsWithoutMerchantJobs
      ).map((job: string) => ({
        value: job,
        label: job,
      })),
    }),
  },
};

const FIELDS: FieldConfigs<TestConsolePropGeneratorOptions, TLinkTokenForm> = {
  mode: {
    Component: InputSelect,
    generateProps: ({ availableModes }) => ({
      required: true,
      options: availableModes.map((value) => ({ value, label: value })),
    }),
  },
  selectedEmployer: {
    Component: InputSelect,
    generateProps: ({ employers, setEmployerSearchText, form }) => ({
      isSearchable: true,
      isClearable: true,
      disabled: !form.mode?.value,
      options: employers
        .map((e) => ({ value: e, label: e.name }))
        .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1)),
      onInputChange: setEmployerSearchText,
    }),
  },
  orgName: {
    Component: InputText,
    getError: (form) => {
      if (form.orgName.length < 3) {
        return 'Org name should have at least 3 characters';
      }
      if (form.orgName.length > 30) {
        return 'Org name should have no more than 30 characters';
      }
      return null;
    },
    generateProps: () => ({
      required: true,
    }),
  },
  platformId: {
    Component: InputText,
    getError: (form) => {
      if (form.platformId && !isValidUUID(form.platformId)) {
        return 'Platform ID should be a valid UUID';
      }
      if (form.platformId && form.selectedEmployer) {
        return 'Platform ID should not be provided when employer is selected';
      }
      return null;
    },
    generateProps: () => ({
      required: false,
    }),
  },
  demandPartnerCustomer: {
    Component: InputSelect,
    generateProps: ({ demandPartnerCustomers, form, setFormFields }) => ({
      required: true,
      label: 'customer',
      options:
        demandPartnerCustomers &&
        demandPartnerCustomers.map((value) => ({
          value,
          label: value.customerWorkspaceName,
        })),
      // when the user selects a customer, set the value of orgName to the customerWorkspaceName
      onInputChange: () => {
        const value = form.demandPartnerCustomer?.value;
        setFormFields({
          demandPartnerCustomer: { value, label: value?.customerWorkspaceName },
          orgName: value?.customerWorkspaceName,
        });
      },
    }),
  },
  requiredJobs: {
    Component: MultiSelect,
    getError: (form) => {
      const employer = form.selectedEmployer?.value;
      const selectedJobs = form.requiredJobs?.map((x) => x.value) || [];

      if (_.isEmpty(employer)) return null;

      const supportedJobs = employer?.supportedJobs || [];
      const unsupportedJob = _.find(selectedJobs, (job) => !supportedJobs.includes(job));

      if (unsupportedJob) return `Employer does not support ${unsupportedJob}`;

      return null;
    },
    generateProps: ({ form }) => ({
      required: !form.accountId,
      /*
       * If supportedJobs return anything that is not supported by the Front-end,
       * it shouldn't display it to the user
       */
      options: _.intersection(
        _.get(form, 'selectedEmployer.value.supportedJobs', ALL_JOBS),
        ALL_JOBS
      ).map((job: string) => ({
        value: job,
        label: job,
      })),
    }),
  },
  amount: {
    Component: InputText,
    getError: (form) => {
      const employer = form.selectedEmployer?.value;
      const amt = +form.amount;
      if (Number.isNaN(amt)) return 'Amount should be a valid number';

      // If employer has min amount and amount is not 0 but less than that return error
      const minAmount = employer?.minAmount || 0;
      if (amt !== 0 && amt < minAmount) return `Amount must be at least $${minAmount}`;

      const maxAmount = employer?.maxAmount || Infinity;
      if (amt > maxAmount) return `Amount must be at most $${maxAmount}`;
      const fractionsOk = employer?.fractionalAmountSupported;
      if (!fractionsOk && !!(amt % 1) && !_.isEmpty(employer))
        return 'This employer does not allow fractional amounts';
      return null;
    },
    generateProps: ({ form, setFormFields }) => {
      return {
        disabled: !shouldEnableAmount(form),
        onChange: (v: string) => {
          setFormFields({ amount: eraseNonDigitsOrNonPeriods(v) });
        },
      };
    },
  },
  accountId: {
    Component: InputText,
    generateProps: ({ setFormFields, lastAccountId }) => {
      return {
        subtitleText: lastAccountId && 'load from last Link session',
        subtitleAction: () => {
          setFormFields({ accountId: lastAccountId });
        },
      };
    },
  },
  endUserId: {
    Component: InputText,
    getError: (form) => {
      if (form.endUserId && form.endUserId.length > 255) {
        return 'End User ID should be less than 255 characters';
      }
      return null;
    },
    generateProps: () => ({
      required: false,
      placeholder: 'If not provided, an id will be auto-generated for token creation',
    }),
  },
  accounts: {
    getError: (form) => {
      try {
        accountSchema.validateSync(form.accounts);
      } catch (err) {
        return ((err as unknown) as any).message;
      }
      return null;
    },
    generateProps: ({ setFormFields, form }) => {
      return {
        required: formHasDDSJob(form),
        disabled: !formHasJobThatAcceptsAccounts(form),
        onChange: (value) => {
          setFormFields({
            accounts: value,
          });
        },
      };
    },
    Component: InputAccount,
  },
  platformMatching: {
    getError: (form) => {
      if (Object.keys(form.platformMatching).length === 0) {
        return null;
      }
      try {
        platformMatchingSchema.validateSync(form.platformMatching);
      } catch (err) {
        return ((err as unknown) as any).message;
      }
      return null;
    },
    generateProps: ({ setFormFields, form }) => {
      return {
        placeholder: {
          mobile_phone_number: '1234567890',
          date_of_birth: '2023-11-22',
          social_security_number: '123412345',
          first_name: 'user',
          last_name: 'multi_payroll',
          home_address_zip_code: '12345',
        },
        required: false,
        disabled: false,
        onChange: (value) => {
          setFormFields({
            platformMatching: value,
          });
        },
        buttonText: 'Add Platform Matching Data',
      };
    },
    Component: InputJSON,
  },
  cards: {
    getError: (form) => {
      if (form?.cards?.length === 0) {
        return null;
      }
      try {
        cardSchema.validateSync(form.cards);
      } catch (err) {
        return ((err as unknown) as any).message;
      }
      return null;
    },
    generateProps: ({ setFormFields, form }) => {
      return {
        required: false,
        disabled: !formHasBillSwitchJob(form),
        onChange: (value) => {
          setFormFields({
            cards: value,
          });
        },
      };
    },
    Component: InputCard,
  },
  platformType: {
    Component: InputSelect,
    generateProps: () => ({
      required: false,
      options: [getEmptyOption(), ...PLATFORM_TYPES.map((type) => getOption(type))],
    }),
  },
  platformTypeWithTax: {
    Component: InputSelect,
    generateProps: () => ({
      required: false,
      options: [getEmptyOption(), ...PLATFORM_TYPES_TAX.map((type) => getOption(type))],
    }),
  },
  documentUploads: {
    Component: InputSelect,
    generateProps: () => ({
      required: false,
      options: [getEmptyOption(), ...FEATURE_LAUNCH_TYPES.map((type) => getOption(type))],
    }),
  },
  depositForms: {
    Component: InputSelect,
    generateProps: () => ({
      required: false,
      options: [getEmptyOption(), ...FEATURE_LAUNCH_TYPES.map((type) => getOption(type))],
    }),
  },
  emptyBox: {
    Component: EmptyBox,
    generateProps: () => ({
      required: false,
      options: [getEmptyOption(), ...FEATURE_LAUNCH_TYPES.map((type) => getOption(type))],
    }),
  },
  language: {
    Component: InputSelect,
    generateProps: () => {
      return {
        options: [getEmptyOption(), { label: 'en', value: 'en' }, { label: 'es', value: 'es' }],
      };
    },
  },
  enableBillNavigator: {
    Component: ({ onChange, checked }) => (
      <Label htmlFor="enableBillNavigator" style={{ width: undefined, flex: 1 }}>
        <Checkbox
          id="enableBillNavigator"
          name="enableBillNavigator"
          onChange={onChange}
          checked={checked}
        />
        enable_bill_navigator
      </Label>
    ),
    generateProps: ({ form, setFormFields }) => ({
      checked: form.enableBillNavigator,
      onChange: () => setFormFields({ enableBillNavigator: !form.enableBillNavigator }),
    }),
  },
  redirectUrl: {
    Component: InputText,
    generateProps: ({ workspaceId }) => ({
      default: getDefaultRedirectUrl(workspaceId),
    }),
    getError: (form) => {
      if (!form.redirectUrl?.toLowerCase().startsWith('https://')) {
        return 'Redirect URL should begin with https://';
      }
      if (!isUrlValid(form.redirectUrl)) {
        return 'Must be a valid URL';
      }
      return null;
    },
  },
  skipIntroScreen: {
    Component: ({ onChange, checked }) => (
      <Label htmlFor="skipIntroScreen" style={{ width: undefined, flex: 1 }}>
        <Checkbox
          id="skipIntroScreen"
          name="skipIntroScreen"
          onChange={onChange}
          checked={checked}
        />
        skip_intro_screen
      </Label>
    ),
    generateProps: ({ form, setFormFields }) => ({
      checked: form.skipIntroScreen,
      onChange: () => setFormFields({ skipIntroScreen: !form.skipIntroScreen }),
    }),
  },
  smartBranchEnabled: {
    Component: ({ onChange, checked }) => (
      <Label htmlFor="smartBranchEnabled" style={{ width: undefined, flex: 1 }}>
        <Checkbox
          id="smartBranchEnabled"
          name="smartBranchEnabled"
          onChange={onChange}
          checked={checked}
        />
        smart_branch_enabled
      </Label>
    ),
    generateProps: ({ form, setFormFields }) => ({
      checked: form.smartBranchEnabled,
      onChange: () => setFormFields({ smartBranchEnabled: !form.smartBranchEnabled }),
    }),
  },
  disableDirectDepositSplitting: {
    Component: ({ onChange, checked }) => (
      <Label htmlFor="disableDirectDepositSplitting" style={{ width: undefined, flex: 1 }}>
        <Checkbox
          id="disableDirectDepositSplitting"
          name="disableDirectDepositSplitting"
          onChange={onChange}
          checked={checked}
        />
        disable_direct_deposit_splitting
      </Label>
    ),
    generateProps: ({ form, setFormFields }) => ({
      checked: form.disableDirectDepositSplitting,
      onChange: () =>
        setFormFields({ disableDirectDepositSplitting: !form.disableDirectDepositSplitting }),
    }),
  },
};

export default FIELDS;
